import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Building, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  MapPin, 
  Phone, 
  Mail,
  Calendar,
  Users,
  TrendingUp,
  MoreVertical,
  AlertTriangle
} from 'lucide-react';
import { facilityAPI } from '../../services/facilityApi';
import { Facility, FacilityQueryParams, type FacilityType, type FacilityStatus } from '../../types/facility';
import { SkeletonGrid } from '../Skeleton/SkeletonLoader';
import { Pagination } from '../Pagination';

const Container = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

const SearchHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;


const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
          }
        `;
      case 'danger':
        return `
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
          }
        `;
      default:
        return `
          background: white;
          color: #374151;
          border: 1px solid #d1d5db;
          &:hover {
            background: #f9fafb;
            border-color: #9ca3af;
          }
        `;
    }
  }}
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  min-width: 300px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  width: 20px;
  height: 20px;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const StatIcon = styled.div<{ color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const StatTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  margin: 0;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #111827;
`;

const FacilitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 24px;
`;

const FacilityCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  overflow: hidden;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const FacilityHeader = styled.div`
  padding: 20px 20px 16px;
  border-bottom: 1px solid #e5e7eb;
`;

const FacilityName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FacilityType = styled.span<{ type: FacilityType }>`
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    const colors = {
      'Hospital': '#dbeafe',
      'Clinic': '#dcfce7',
      'Nursing Home': '#fef3c7',
      'Rehabilitation Center': '#e0e7ff',
      'Urgent Care': '#fce7f3',
      'Specialty Center': '#f3e8ff',
      'Post Acute': '#f0f9ff',
      'Healthcare Center': '#ecfdf5',
      'Care Center': '#fef7ed',
      'Wellness Center': '#fdf2f8',
      'Other': '#f3f4f6'
    };
    return colors[props.type] || '#f3f4f6';
  }};
  color: ${props => {
    const colors = {
      'Hospital': '#1e40af',
      'Clinic': '#166534',
      'Nursing Home': '#92400e',
      'Rehabilitation Center': '#3730a3',
      'Urgent Care': '#be185d',
      'Specialty Center': '#7c3aed',
      'Post Acute': '#0c4a6e',
      'Healthcare Center': '#14532d',
      'Care Center': '#9a3412',
      'Wellness Center': '#be185d',
      'Other': '#374151'
    };
    return colors[props.type] || '#374151';
  }};
`;

const FacilityStatus = styled.span<{ status: FacilityStatus }>`
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    const colors = {
      'Active': '#dcfce7',
      'Inactive': '#fee2e2',
      'Pending': '#fef3c7',
      'Suspended': '#f3f4f6'
    };
    return colors[props.status] || '#f3f4f6';
  }};
  color: ${props => {
    const colors = {
      'Active': '#166534',
      'Inactive': '#dc2626',
      'Pending': '#92400e',
      'Suspended': '#374151'
    };
    return colors[props.status] || '#374151';
  }};
`;

const FacilityBody = styled.div`
  padding: 16px 20px;
`;

const FacilityInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 14px;
  color: #6b7280;
`;

const FacilityFooter = styled.div`
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 8px 12px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: #3b82f6;
          color: white;
          &:hover {
            background: #2563eb;
          }
        `;
      case 'danger':
        return `
          background: #ef4444;
          color: white;
          &:hover {
            background: #dc2626;
          }
        `;
      default:
        return `
          background: #f3f4f6;
          color: #374151;
          &:hover {
            background: #e5e7eb;
          }
        `;
    }
  }}
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #dc2626;
  padding: 16px;
  border-radius: 8px;
  margin: 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
`;

const EmptyIcon = styled(Building)`
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  color: #d1d5db;
`;

const FacilitiesList: React.FC = () => {
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<FacilityType | ''>('');
  const [statusFilter, setStatusFilter] = useState<FacilityStatus | ''>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [totalItems, setTotalItems] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    pending: 0
  });

  useEffect(() => {
    loadFacilities();
    loadStats();
  }, [searchTerm, typeFilter, statusFilter, currentPage, itemsPerPage]);

  const loadFacilities = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: FacilityQueryParams = {
        search: searchTerm || undefined,
        type: typeFilter || undefined,
        status: statusFilter || undefined,
        page: currentPage,
        limit: itemsPerPage
      };
      
      const response = await facilityAPI.getFacilities(params);
      setFacilities(response.facilities);
      setTotalItems(response.total || response.facilities.length);
    } catch (err) {
      setError('Failed to load facilities');
      console.error('Error loading facilities:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await facilityAPI.getFacilityStatistics();
      setStats({
        total: statsData.totalFacilities,
        active: statsData.activeFacilities,
        inactive: statsData.inactiveFacilities,
        pending: 0 // Pending is not a facility type, it's a status
      });
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this facility?')) {
      try {
        await facilityAPI.deleteFacility(id);
        await loadFacilities();
        await loadStats();
      } catch (err) {
        setError('Failed to delete facility');
        console.error('Error deleting facility:', err);
      }
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  if (loading) {
    return (
      <Container>
        <SkeletonGrid items={6} columns={3} cardHeight="200px" />
      </Container>
    );
  }

  return (
    <Container>
      <StatsGrid>
        <StatCard>
          <StatHeader>
            <StatIcon color="#3b82f6">
              <Building size={20} />
            </StatIcon>
            <StatTitle>Total Facilities</StatTitle>
          </StatHeader>
          <StatValue>{stats.total}</StatValue>
        </StatCard>
        
        <StatCard>
          <StatHeader>
            <StatIcon color="#10b981">
              <TrendingUp size={20} />
            </StatIcon>
            <StatTitle>Active Facilities</StatTitle>
          </StatHeader>
          <StatValue>{stats.active}</StatValue>
        </StatCard>
        
        <StatCard>
          <StatHeader>
            <StatIcon color="#f59e0b">
              <Calendar size={20} />
            </StatIcon>
            <StatTitle>Pending</StatTitle>
          </StatHeader>
          <StatValue>{stats.pending}</StatValue>
        </StatCard>
        
        <StatCard>
          <StatHeader>
            <StatIcon color="#ef4444">
              <Users size={20} />
            </StatIcon>
            <StatTitle>Inactive</StatTitle>
          </StatHeader>
          <StatValue>{stats.inactive}</StatValue>
        </StatCard>
      </StatsGrid>

      <SearchHeader>
        <SearchContainer>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder="Search facilities by name, city, or state..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </SearchContainer>
        
        <FilterContainer>
          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as FacilityType | '')}
          >
            <option value="">All Types</option>
            <option value="Hospital">Hospital</option>
            <option value="Clinic">Clinic</option>
            <option value="Nursing Home">Nursing Home</option>
            <option value="Rehabilitation Center">Rehabilitation Center</option>
            <option value="Urgent Care">Urgent Care</option>
            <option value="Specialty Center">Specialty Center</option>
            <option value="Other">Other</option>
          </Select>
          
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as FacilityStatus | '')}
          >
            <option value="">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Pending">Pending</option>
            <option value="Suspended">Suspended</option>
          </Select>
        </FilterContainer>
      </SearchHeader>

      {error && (
        <ErrorMessage>
          <AlertTriangle size={20} />
          {error}
        </ErrorMessage>
      )}

      {facilities.length === 0 ? (
        <EmptyState>
          <EmptyIcon />
          <h3>No facilities found</h3>
          <p>Try adjusting your search criteria or add a new facility.</p>
        </EmptyState>
      ) : (
        <>
          <FacilitiesGrid>
            {facilities.map((facility) => (
            <FacilityCard key={facility.id}>
              <FacilityHeader>
                <FacilityName>
                  {facility.name}
                </FacilityName>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <FacilityType type={facility.type}>
                    {facility.type}
                  </FacilityType>
                  <FacilityStatus status={facility.status}>
                    {facility.status}
                  </FacilityStatus>
                </div>
              </FacilityHeader>
              
              <FacilityBody>
                <FacilityInfo>
                  <MapPin size={16} />
                  {facility.address.street}, {facility.address.city}, {facility.address.state} {facility.address.zipCode}
                </FacilityInfo>
                
                <FacilityInfo>
                  <Phone size={16} />
                  {facility.contact.phone}
                </FacilityInfo>
                
                <FacilityInfo>
                  <Mail size={16} />
                  {facility.contact.email}
                </FacilityInfo>
                
                {facility.capacity && (
                  <FacilityInfo>
                    <Users size={16} />
                    Capacity: {facility.capacity}
                  </FacilityInfo>
                )}
              </FacilityBody>
              
              <FacilityFooter>
                <div>
                  <small style={{ color: '#6b7280' }}>
                    Established: {new Date(facility.establishedDate).toLocaleDateString()}
                  </small>
                </div>
                
                <ActionButtons>
                  <ActionButton variant="secondary">
                    <Eye size={16} />
                    View
                  </ActionButton>
                  <ActionButton variant="primary">
                    <Edit size={16} />
                    Edit
                  </ActionButton>
                  <ActionButton variant="danger" onClick={() => handleDelete(facility.id)}>
                    <Trash2 size={16} />
                    Delete
                  </ActionButton>
                </ActionButtons>
              </FacilityFooter>
            </FacilityCard>
          ))}
        </FacilitiesGrid>
        
        {totalItems > itemsPerPage && (
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            showPageSizeSelector={true}
            pageSizeOptions={[6, 12, 24, 48, 100]}
            showInfo={true}
          />
        )}
        </>
      )}
    </Container>
  );
};

export default FacilitiesList;
